import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeviceMap from "./DeviceMap";
import VenueFacts from "./VenueFacts";
import { API_BASE_URL } from "../../config";
import "../../styles/Browse.css";

const PAGE_SIZE = 10;

// The screen list was a six-column table with two buttons per row inside a
// 700px container, coloured with #4f8cff and #ea476e — a blue from no palette
// and the pre-tokens brand pink. Screens are independent things being compared
// against each other, not rows in a ledger, so they are cards now, which is
// also the only shape that survives a phone.
//
// The behaviour is unchanged apart from one thing that was wrong: the batch
// add-to-cart awaited every request and then reported success regardless of
// what came back, so a rejected add looked identical to a successful one.

function money(value) {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n.toFixed(2);
}

// `listing_state` comes from the screen's own check-ins (utils/liveness.py),
// NOT from Device.status — that column is written by a job nothing runs, so it
// reported screens as "active" months after they were unplugged. The words here
// are deliberately about whether a booking would play, because that is what the
// person reading this list is about to pay for.
const LISTING = {
    live: { label: "Online", tone: "ok" },
    quiet: { label: "Not checking in", tone: "danger" },
    never_seen: { label: "Never come online", tone: "danger" },
    paused: { label: "Switched off", tone: "warn" },
};

function StatusBadge({ device }) {
    const state = LISTING[device.listing_state];
    if (!state) return null;
    return <span className={`wb-badge wb-badge--${state.tone}`}>{state.label}</span>;
}

export default function DeviceSummaryPage() {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cartDeviceIds, setCartDeviceIds] = useState(new Set());
    const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [status, setStatus] = useState("");
    const [adding, setAdding] = useState(false);
    // Off by default: the list is screens a booking would actually play on.
    // Anything else — never came online, stopped checking in, switched off by
    // its owner, or never priced — is something to opt into, because a booking
    // on one plays nowhere.
    const [includeWaiting, setIncludeWaiting] = useState(false);
    // Two pieces of state, not one. `draft` is what is in the box; `query` is
    // what the last submit asked for. Searching on every keystroke would fire a
    // request per character at an endpoint that is rate limited and uncached.
    const [draft, setDraft] = useState("");
    const [query, setQuery] = useState("");
    const isCustomer = localStorage.getItem("role") === "customer";

    useEffect(() => {
        async function fetchDevices() {
            setLoading(true);
            try {
                const res = await fetch(
                    `${API_BASE_URL}/api/devices/summary/active?page=${page}` +
                    `&page_size=${PAGE_SIZE}&status=${includeWaiting ? "all" : "active"}` +
                    (query ? `&q=${encodeURIComponent(query)}` : "")
                );
                const data = await res.json();
                setDevices(data.devices || []);
                setTotal(data.total || 0);
            } catch {
                setDevices([]);
                setTotal(0);
            }
            setLoading(false);
        }
        fetchDevices();
    }, [page, includeWaiting, query]);

    useEffect(() => {
        if (!isCustomer) return;
        fetch(`${API_BASE_URL}/api/customer/cart`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
            .then((res) => res.json())
            .then((data) => {
                const ids = new Set((Array.isArray(data) ? data : []).map((item) => item.device_id));
                setCartDeviceIds(ids);
            })
            .catch(() => setCartDeviceIds(new Set()));
    }, [isCustomer]);

    const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

    const handleSelect = (deviceId) => {
        setSelectedDeviceIds((prev) => {
            const next = new Set(prev);
            if (next.has(deviceId)) next.delete(deviceId);
            else next.add(deviceId);
            return next;
        });
    };

    async function handleBatchAddToCart() {
        setAdding(true);
        setStatus("");
        const ids = Array.from(selectedDeviceIds);

        // Dates default to today-tomorrow and no ad is chosen yet; the cart
        // flags both. No rate is sent -- it used to hardcode `proposed_rate: 25`,
        // which was neither the screen's rate nor what would be charged.
        const results = await Promise.all(
            ids.map((deviceId) =>
                fetch(`${API_BASE_URL}/api/customer/cart`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    body: JSON.stringify({
                        device_id: deviceId,
                        image_id: null,
                        start_time: getNowLocalDateTime(),
                        end_time: getTomorrowLocalDateTime(),
                    }),
                })
                    .then((res) => ({ deviceId, ok: res.ok }))
                    .catch(() => ({ deviceId, ok: false }))
            )
        );

        // This used to be Promise.all(...) followed unconditionally by "Added
        // selected devices to cart!". A rejected add and a successful one were
        // reported the same way, which is how a customer ends up at checkout
        // with fewer screens than they picked.
        const added = results.filter((r) => r.ok);
        const failed = results.filter((r) => !r.ok);

        if (added.length) {
            setCartDeviceIds((prev) => {
                const next = new Set(prev);
                added.forEach((r) => next.add(r.deviceId));
                return next;
            });
        }
        setSelectedDeviceIds(new Set(failed.map((r) => r.deviceId)));

        if (!failed.length) {
            setStatus(`Added ${added.length} ${added.length === 1 ? "screen" : "screens"} to your cart.`);
        } else if (!added.length) {
            setStatus("None of those could be added. They are still selected — try again.");
        } else {
            setStatus(
                `Added ${added.length}. ${failed.length} could not be added and ${failed.length === 1 ? "is" : "are"} still selected.`
            );
        }
        setAdding(false);
    }

    if (loading) {
        return <div className="br"><p className="wb-sub">Loading screens…</p></div>;
    }

    return (
        <div className="br">
            <header className="br__head">
                <h1>Explore locations</h1>
                <p>
                    Screens inside local businesses — salons, cafés, gyms, studios,
                    waiting rooms. Book by the day, at the rate on the listing.
                </p>
            </header>

            <form
                className="br__search"
                onSubmit={(e) => {
                    e.preventDefault();
                    setPage(1);
                    setQuery(draft.trim());
                }}
                role="search"
            >
                <input
                    className="wb-input br__search-input"
                    type="search"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Screen code (A003), name or location"
                    aria-label="Search screens by code, name or location"
                />
                <button type="submit" className="wb-btn wb-btn--primary">
                    Search
                </button>
                {query && (
                    <button
                        type="button"
                        className="wb-btn wb-btn--ghost"
                        onClick={() => {
                            setDraft("");
                            setQuery("");
                            setPage(1);
                        }}
                    >
                        Clear
                    </button>
                )}
            </form>

            <div className="br__map">
                <DeviceMap devices={devices} />
            </div>

            <label className="br__filter">
                <input
                    type="checkbox"
                    checked={includeWaiting}
                    onChange={(e) => {
                        // Back to page 1: the result set changes size, so
                        // staying on page 4 can land on an empty page that
                        // looks like "no screens".
                        setPage(1);
                        setIncludeWaiting(e.target.checked);
                    }}
                />
                Include screens that cannot be booked right now
            </label>

            {devices.length === 0 ? (
                <div className="wb-empty">
                    {query ? (
                        <>
                            Nothing matches <strong>{query}</strong>.{" "}
                            {!includeWaiting &&
                                "It may be a screen that has not checked in yet — tick the box above to include those."}
                        </>
                    ) : includeWaiting ? (
                        <>
                            No screens are listed yet.{" "}
                            <Link to="/waitlist">Have one to list?</Link>
                        </>
                    ) : (
                        <>
                            No screens are online right now. Tick the box above to
                            include ones that have not checked in yet.
                        </>
                    )}
                </div>
            ) : (
                <>
                    <p className="br__count">
                        {total} {total === 1 ? "screen" : "screens"}
                    </p>

                    <div className="br__grid">
                        {devices.map((device) => {
                            const inCart = cartDeviceIds.has(device.id);
                            const selected = selectedDeviceIds.has(device.id);
                            const rate = money(device.daily_rate);

                            return (
                                <article
                                    className={"br__card" + (selected ? " is-selected" : "")}
                                    key={device.id}
                                >
                                    <div className="br__card-top">
                                        <h2 className="br__name">
                                            {device.name || "Unnamed screen"}
                                        </h2>
                                        <StatusBadge device={device} />
                                    </div>

                                    {device.unavailable_reason && (
                                        <p className="wb-note wb-note--warn" style={{ margin: "0 0 0.5rem" }}>
                                            {device.unavailable_reason}
                                        </p>
                                    )}

                                    {/* The same code the screen shows while it
                                        is unbooked, so someone who saw it in a
                                        window can find this listing. */}
                                    {device.public_code && (
                                        <p className="br__code">{device.public_code}</p>
                                    )}

                                    <p className="br__addr">
                                        {device.address || "Location not given"}
                                    </p>

                                    {/* What kind of room, and who is in it. This is
                                        what an advertiser is actually choosing
                                        between -- before it, a busy salon and a
                                        quiet one looked identical here.

                                        Rendered only when the host has said. A
                                        screen with nothing here shows nothing,
                                        rather than a plausible middle value: an
                                        invented figure is indistinguishable from a
                                        declared one once it is on the card, and
                                        this one has a price attached. */}
                                    <VenueFacts device={device} />

                                    {/* Only rendered when there is a business name. The
                                        endpoint also sends vendor_username, but users no
                                        longer have a username column, so it is always
                                        null -- this used to print an empty "Vendor" cell. */}
                                    {device.vendor_business_name && (
                                        <p className="br__vendor">Listed by {device.vendor_business_name}</p>
                                    )}

                                    <div className="br__spacer" />

                                    <div className="br__rate">
                                        {rate ? (
                                            <>
                                                <span className="br__rate-amount">${rate}</span>
                                                <span className="br__rate-unit">per day</span>
                                            </>
                                        ) : (
                                            <span className="br__rate--none">
                                                No rate set — not bookable yet
                                            </span>
                                        )}
                                    </div>

                                    <div className="br__actions">
                                        <Link
                                            to={`/devices/${device.id}`}
                                            className="wb-btn wb-btn--secondary"
                                        >
                                            View details
                                        </Link>
                                        {isCustomer && rate && (
                                            <Link
                                                to={`/devices/${device.id}?request=1`}
                                                className="wb-btn wb-btn--primary"
                                            >
                                                Book
                                            </Link>
                                        )}
                                    </div>

                                    {isCustomer && (
                                        <label className={"br__pick" + (inCart || !rate ? " is-disabled" : "")}>
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => handleSelect(device.id)}
                                                disabled={inCart || !rate}
                                            />
                                            {inCart
                                                ? "Already in your cart"
                                                : !rate
                                                    ? "Cannot be added without a rate"
                                                    : "Add to selection"}
                                        </label>
                                    )}
                                </article>
                            );
                        })}
                    </div>

                    {isCustomer && selectedDeviceIds.size > 0 && (
                        <div className="br__bar">
                            <div>
                                <div className="br__bar-count">
                                    {selectedDeviceIds.size} selected
                                </div>
                                <div className="br__bar-note">
                                    Added for today and tomorrow with no ad chosen — set both
                                    in the cart.
                                </div>
                            </div>
                            <button
                                type="button"
                                className="wb-btn wb-btn--primary"
                                onClick={handleBatchAddToCart}
                                disabled={adding}
                            >
                                {adding ? "Adding…" : "Add to cart"}
                            </button>
                        </div>
                    )}

                    {status && <div className="wb-note" style={{ marginTop: "1rem" }}>{status}</div>}

                    {totalPages > 1 && (
                        <div className="br__pager">
                            <button
                                type="button"
                                className="wb-btn wb-btn--secondary wb-btn--sm"
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                            >
                                Previous
                            </button>
                            <span className="br__pager-label">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                type="button"
                                className="wb-btn wb-btn--secondary wb-btn--sm"
                                onClick={() => setPage(page + 1)}
                                disabled={page >= totalPages}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function getNowLocalDateTime() {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, "0");
    return (
        now.getFullYear() + "-" + pad(now.getMonth() + 1) + "-" + pad(now.getDate()) +
        "T" + pad(now.getHours()) + ":" + pad(now.getMinutes())
    );
}

function getTomorrowLocalDateTime() {
    const now = new Date();
    now.setDate(now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, "0");
    return (
        now.getFullYear() + "-" + pad(now.getMonth() + 1) + "-" + pad(now.getDate()) +
        "T" + pad(now.getHours()) + ":" + pad(now.getMinutes())
    );
}
